import type { CarouselProps as NCarouselProps } from 'nuka-carousel';
import NCarousel from 'nuka-carousel';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '@hotelplan/components.common.icon';
import { useElementSize } from '@hotelplan/libs.hooks-dom';

const getSlidesToShow = (
  windowWidth: number,
  width: number,
  slideWidth: number,
  slideGap = 0
): number => {
  let carouselWidth = width || windowWidth;
  if (width > 1350) carouselWidth = 1350;
  else if (width > 1170) carouselWidth = 1170;
  if (width > 0 && carouselWidth > width) {
    carouselWidth = width;
  }
  return Math.floor((carouselWidth + slideGap) / (slideWidth + slideGap));
};

export interface ICarouselProps {
  slideIndex?: number;
  slideWidth?: number;
  slideGap?: number;
  className?: string;
}

const StyledChevronIcon = styled(Icon)(({ theme: { icons } }) => icons.lg, {
  cursor: 'pointer',
  width: '24px',
  height: '24px',
  color: 'black',
});

const NCarouselWrapper = styled.div<{
  slideWidth: number;
  slideGap: number;
}>(({ slideWidth, slideGap, theme: { space } }) => ({
  width: '100%',
  '.slider > .slider-frame': {
    padding: space[2],
    marginLeft: -space[2],
    marginTop: -space[2],
  },
  '.slider, .slider-list, .slider-slide': {
    outline: 'none',
    cursor: 'default!important',
  },
  maxWidth: 6 * (slideWidth + slideGap) - slideGap,
  '@media screen and (max-width: 1350px)': {
    maxWidth: 5 * (slideWidth + slideGap) - slideGap,
  },
  '@media screen and (max-width: 1170px)': {
    maxWidth: 4 * (slideWidth + slideGap) - slideGap,
  },
}));

const Carousel: React.FC<
  React.PropsWithChildren<ICarouselProps> & {
    children?: React.ReactNode | React.ReactNode[];
  }
> = ({
  slideIndex = 1,
  slideWidth = 170,
  slideGap = 30,
  className,
  children,
}) => {
  const [slidesToShow, setSlidesToShow] = useState(
    getSlidesToShow(
      typeof window !== 'undefined' ? window.innerWidth : 0,
      0,
      slideWidth,
      slideGap
    )
  );

  const childrenCount =
    (typeof children === 'object' &&
      children.constructor === Array &&
      children.length) ||
    0;

  const carouselWrapperRef = useRef<HTMLDivElement>(null);
  const { w } = useElementSize(carouselWrapperRef, { throttle: 300 });

  useEffect(() => {
    setSlidesToShow(
      getSlidesToShow(window.innerWidth, w, slideWidth, slideGap)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w]);

  const carouselSettings = useMemo<NCarouselProps>(() => {
    return {
      dragging: false,
      slidesToShow: slidesToShow,
      slideIndex: slideIndex > slidesToShow ? slideIndex - slidesToShow : 0,
      renderBottomCenterControls: null,
      slideWidth: `${slideWidth + slideGap}px`,
      cellAlign: `left`,
      renderCenterLeftControls: function renderCenterLeftControls({
        currentSlide,
        previousSlide,
      }: {
        currentSlide: number;
        previousSlide: () => void;
      }) {
        if (currentSlide === 0) return null;

        return (
          <button onClick={previousSlide}>
            <StyledChevronIcon name="chevron-long-left" />
          </button>
        );
      },
      renderCenterRightControls: function renderCenterRightControls({
        currentSlide,
        slideCount,
        nextSlide,
      }: {
        currentSlide: number;
        slideCount: number;
        nextSlide: () => void;
      }) {
        if (
          currentSlide + slidesToShow === slideCount ||
          slideCount <= slidesToShow
        )
          return null;

        return (
          <button onClick={nextSlide}>
            <StyledChevronIcon name="chevron-long-right" />
          </button>
        );
      },
      getControlsContainerStyles: function getControlsContainerStyles(
        key: string
      ) {
        switch (key) {
          case 'CenterRight':
            return {
              right: '-70px',
            };

          case 'CenterLeft':
            return {
              left: '-70px',
            };

          default:
            return {};
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slidesToShow, childrenCount]);

  return (
    <NCarouselWrapper
      ref={carouselWrapperRef}
      slideWidth={slideWidth}
      slideGap={slideGap}
      data-id="carousel"
      className={className}
    >
      {/*@ts-ignore*/}
      <NCarousel {...carouselSettings} data-id="carousel-core">
        {children}
      </NCarousel>
    </NCarouselWrapper>
  );
};

export default Carousel;
